import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import FormField from '../common/FormField';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { MemberFilterParams, MembershipType, WorkSector } from '../../types/email';
import { IndividualMemberDto, getMemberDisplayFirstName, getMemberDisplayLastName } from '../../types/member';
import { adminService } from '../../services/admin';
import { PagedResponse } from '../../types/member';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const FilterCard = styled(Card)`
  padding: ${spacing[6]};
`;

const FilterTitle = styled.h3`
  margin: 0 0 ${spacing[4]} 0;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.display};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[4]};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
  align-items: flex-end;

  > * {
    flex: 1 1 140px;
    min-width: min(100%, 140px);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
`;

const CheckboxInput = styled.input`
  cursor: pointer;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${spacing[4]};
  padding-top: ${spacing[4]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;

const SelectedCount = styled.div`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
`;

const PreviewCard = styled(Card)`
  padding: ${spacing[6]};
  max-height: 400px;
  overflow-y: auto;
`;

const PreviewTitle = styled.h4`
  margin: 0 0 ${spacing[4]} 0;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
`;

const PreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const PreviewItem = styled.div`
  padding: ${spacing[2]} ${spacing[3]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
`;

interface MemberFilterProps {
  filters: MemberFilterParams;
  onFiltersChange: (filters: MemberFilterParams) => void;
}

export const MemberFilter: React.FC<MemberFilterProps> = ({
  filters,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [localFilters, setLocalFilters] = useState<MemberFilterParams>(filters);
  const [memberCount, setMemberCount] = useState<number>(0);
  const [previewMembers, setPreviewMembers] = useState<IndividualMemberDto[]>([]);
  const [loading, setLoading] = useState(false);

  const updateFilter = useCallback((key: keyof MemberFilterParams, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    const cleared: MemberFilterParams = {
      page: 1,
      pageSize: 10,
    };
    setLocalFilters(cleared);
    onFiltersChange(cleared);
  }, [onFiltersChange]);

  const applyFilters = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams: MemberFilterParams = {
        ...localFilters,
        page: 1,
        pageSize: 100, // Get more for preview
      };
      
      const result: PagedResponse<IndividualMemberDto> = await adminService.getFilteredMembers(filterParams);
      setMemberCount(result.total);
      setPreviewMembers(result.items.slice(0, 10)); // Show first 10
      onFiltersChange(localFilters);
    } catch (error) {
      console.error('Error filtering members:', error);
    } finally {
      setLoading(false);
    }
  }, [localFilters, onFiltersChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 500);

    return () => clearTimeout(timer);
  }, [localFilters]);

  return (
    <Container>
      <FilterCard variant="elevated">
        <FilterTitle>{t('email.filterMembers')}</FilterTitle>
        
        <FilterGrid>
          <FormField
            label={t('email.search')}
            type="text"
            value={localFilters.search || ''}
            onChange={(e) => updateFilter('search', e.target.value || undefined)}
            placeholder={t('email.searchPlaceholder')}
          />

          <FormField
            label={t('membership.type')}
            select
            value={localFilters.membershipType?.toString() || ''}
            onChange={(e) => updateFilter('membershipType', e.target.value ? parseInt(e.target.value) : undefined)}
            options={[
              { value: '', label: t('admin.all') },
              { value: MembershipType.Regular.toString(), label: t('membership.regular') },
              { value: MembershipType.Student.toString(), label: t('membership.student') },
              { value: MembershipType.Retired.toString(), label: t('membership.retired') },
              { value: MembershipType.Honorary.toString(), label: t('membership.honorary') },
            ]}
          />

          <FormField
            label={t('workSector.title')}
            select
            value={localFilters.workSector?.toString() || ''}
            onChange={(e) => updateFilter('workSector', e.target.value ? parseInt(e.target.value) : undefined)}
            options={[
              { value: '', label: t('admin.all') },
              { value: WorkSector.Museology.toString(), label: t('workSector.museology') },
              { value: WorkSector.Education.toString(), label: t('workSector.education') },
              { value: WorkSector.Documentation.toString(), label: t('workSector.documentation') },
              { value: WorkSector.Conservation.toString(), label: t('workSector.conservation') },
              { value: WorkSector.Exhibition.toString(), label: t('workSector.exhibition') },
              { value: WorkSector.Other.toString(), label: t('workSector.other') },
            ]}
          />

          <FilterRow>
            <FormField
              label={t('email.registrationYearFrom')}
              type="number"
              value={localFilters.registrationYearFrom?.toString() || ''}
              onChange={(e) => updateFilter('registrationYearFrom', e.target.value ? parseInt(e.target.value) : undefined)}
            />
            <FormField
              label={t('email.registrationYearTo')}
              type="number"
              value={localFilters.registrationYearTo?.toString() || ''}
              onChange={(e) => updateFilter('registrationYearTo', e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </FilterRow>

          <FormField
            label={t('email.mainCommittee')}
            type="text"
            value={localFilters.mainCommittee || ''}
            onChange={(e) => updateFilter('mainCommittee', e.target.value || undefined)}
            placeholder={t('email.committeePlaceholder')}
          />

          <FormField
            label={t('email.committeeIsMain')}
            select
            value={localFilters.committeeIsMain === undefined ? '' : localFilters.committeeIsMain ? 'main' : 'secondary'}
            onChange={(e) => {
              const v = e.target.value;
              updateFilter('committeeIsMain', v === '' ? undefined : v === 'main');
            }}
            options={[
              { value: '', label: t('admin.all') },
              { value: 'main', label: t('email.committeeMain') },
              { value: 'secondary', label: t('email.committeeSecondary') },
            ]}
          />

          <FormField
            label={t('email.residentialCity')}
            type="text"
            value={localFilters.residentialCity || ''}
            onChange={(e) => updateFilter('residentialCity', e.target.value || undefined)}
          />

          <FormField
            label={t('email.residentialCountry')}
            type="text"
            value={localFilters.residentialCountry || ''}
            onChange={(e) => updateFilter('residentialCountry', e.target.value || undefined)}
          />

          <FormField
            label={t('email.professionalCity')}
            type="text"
            value={localFilters.professionalCity || ''}
            onChange={(e) => updateFilter('professionalCity', e.target.value || undefined)}
          />
        </FilterGrid>

        <CheckboxGroup>
          <CheckboxLabel>
            <CheckboxInput
              type="checkbox"
              checked={localFilters.isGraduateStudent || false}
              onChange={(e) => updateFilter('isGraduateStudent', e.target.checked || undefined)}
            />
            {t('email.graduateStudent')}
          </CheckboxLabel>
          <CheckboxLabel>
            <CheckboxInput
              type="checkbox"
              checked={localFilters.isRetired || false}
              onChange={(e) => updateFilter('isRetired', e.target.checked || undefined)}
            />
            {t('email.retired')}
          </CheckboxLabel>
          <CheckboxLabel>
            <CheckboxInput
              type="checkbox"
              checked={localFilters.isHonoraryMember || false}
              onChange={(e) => updateFilter('isHonoraryMember', e.target.checked || undefined)}
            />
            {t('email.honoraryMember')}
          </CheckboxLabel>
          <CheckboxLabel>
            <CheckboxInput
              type="checkbox"
              checked={localFilters.isActive !== undefined ? localFilters.isActive : true}
              onChange={(e) => updateFilter('isActive', e.target.checked)}
            />
            {t('email.activeOnly')}
          </CheckboxLabel>
        </CheckboxGroup>

        <ActionsRow>
          <SelectedCount>
            {loading
              ? t('email.loading')
              : `${memberCount} ${t('email.membersSelected')}`}
          </SelectedCount>
          <Button variant="outline" onClick={clearFilters}>
            {t('email.clearFilters')}
          </Button>
        </ActionsRow>
      </FilterCard>

      {previewMembers.length > 0 && (
        <PreviewCard variant="elevated">
          <PreviewTitle>
            {t('email.preview')} ({previewMembers.length} {t('email.of')} {memberCount})
          </PreviewTitle>
          <PreviewList>
            {previewMembers.map((member) => (
              <PreviewItem key={member.id}>
                <strong>{getMemberDisplayFirstName(member)} {getMemberDisplayLastName(member)}</strong> - {member.email}
              </PreviewItem>
            ))}
          </PreviewList>
        </PreviewCard>
      )}
    </Container>
  );
};

export default MemberFilter;
